import type { IPublicClientApplication } from "@azure/msal-browser";
import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid";
import {
    type AcquireTokenOptions,
    createMsalTokenSource,
    getRegisteredTokenSource,
} from "./authToken";
import { postCopilotDebug } from "./copilotDebug";

interface FetchParams {
    url: string;
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    payload?: Record<string, unknown> | Record<string, unknown>[];
    instance: IPublicClientApplication;
    expectedStatus?: number;
}

type isForbiddenGetRequestInput = {
    response: Response;
    options: RequestInit;
    endpoint: string;
};

type ResponseHandlerInput = {
    response: Response;
    options: RequestInit;
    url?: string;
    expectedStatus?: number;
};

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
// Simple health check cache
let healthCheckCache = {
    isHealthy: true,
    timestamp: 0,
};

const CACHE_DURATION = 30000; // 30 seconds cache for health check results (should be enough until the user logs in to Zscaler)
export const FORBIDDEN_KEY = "isForbidden";

/**
 * Thrown when no access token could be obtained. The request is never sent:
 * the backend rejects `Bearer null` anyway, and the token source has already
 * kicked off the recovery (redirect in the SPA, `cts-auth-required` in embeds).
 */
export class AuthRequiredError extends Error {
    constructor(url?: string) {
        const endpoint = url ? new URL(url).pathname : "";
        super(`Request to ${endpoint} skipped: authentication is required`);
        this.name = "AuthRequiredError";
    }
}

const setForbiddenState = (isForbidden = true): void => {
    localStorage.setItem(FORBIDDEN_KEY, isForbidden.toString());
};

const createHeaders = (token: string) => {
    const transactionId = uuidv4();

    Sentry.withScope((scope) => {
        scope.setTag("transaction_id", transactionId);
    });
    return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Transaction-ID": transactionId,
    };
};

const isForbiddenGetRequest = ({
    response,
    options,
    endpoint,
}: isForbiddenGetRequestInput): boolean =>
    response.status === 403 && options.method === "GET" && endpoint.includes("/v1/user");

export const handleResponse = async ({
    response,
    options,
    url,
    expectedStatus,
}: ResponseHandlerInput): Promise<Response> => {
    const endpoint = url ? new URL(url).pathname : "";

    postCopilotDebug("api", `${options.method} ${endpoint} → ${response.status}`);

    if (!response.ok) {
        if (isForbiddenGetRequest({ response, options, endpoint })) {
            setForbiddenState(true);
        }

        throw new Error(`Request to ${endpoint} failed with status ${response.status}`);
    }

    if (expectedStatus !== undefined && response.status !== expectedStatus) {
        throw new Error(`Request to ${endpoint} returned unexpected status ${response.status}`);
    }

    return response;
};

export const postAPI = async ({
    url,
    payload,
    instance,
    expectedStatus,
}: Omit<FetchParams, "method">): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });
        if (!token) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new AuthRequiredError(url);
        }

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        const headers = createHeaders(token);
        const options = {
            headers,
            method: "POST",
            body: JSON.stringify(payload),
        };

        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

export const postFormAPI = async ({
    url,
    formData,
    instance,
    expectedStatus,
}: Omit<FetchParams, "method" | "payload"> & { formData: FormData }): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });
        if (!token) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new AuthRequiredError(url);
        }

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        // Content-Type is left to the browser so the multipart boundary is set.
        const { "Content-Type": _contentType, ...headers } = createHeaders(token);
        const options = {
            headers,
            method: "POST",
            body: formData,
        };

        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

/**
 * Acquires an access token for an API call.
 *
 * The token comes from whatever source is registered for the current surface
 * (see `authToken.ts`): an MSAL-backed source for the main SPA, or a
 * parent-supplied-token / popup-free MSAL source for the widget & copilot
 * embeds. When nothing is registered yet we fall back to an MSAL source built
 * from the passed `instance`, so existing callers keep working unchanged.
 *
 * Two safeguards for embedded (iframe) usage live here and in the MSAL source:
 *
 *   1. Single-flight: concurrent callers share one in-flight acquisition, so a
 *      surface never starts two interactive flows at once.
 *   2. interaction_in_progress recovery + no background popups: the MSAL source
 *      never opens `acquireTokenPopup` from a fetch - it signals the embed's
 *      auth provider (`cts-auth-required`) and returns null instead.
 *
 * @returns The acquired token, or null when interaction must be handled elsewhere
 */

// Shared in-flight acquisitions, keyed by whether a forced refresh was requested.
// A forced refresh must run its own request; a silent caller can piggyback on an
// in-flight silent OR forced request.
const inFlightTokenRequests: Record<"silent" | "force", Promise<string | null> | null> = {
    silent: null,
    force: null,
};

export const acquireAuthToken = async (
    instance: IPublicClientApplication,
    options: AcquireTokenOptions = {},
): Promise<string | null> => {
    const forceRefresh = options.forceRefresh ?? false;
    const key = forceRefresh ? "force" : "silent";

    const existing =
        inFlightTokenRequests[key] ?? (key === "silent" ? inFlightTokenRequests.force : null);
    if (existing) {
        return existing;
    }

    const source = getRegisteredTokenSource() ?? createMsalTokenSource(instance);
    const request = source({ forceRefresh });
    inFlightTokenRequests[key] = request;
    void request.finally(() => {
        if (inFlightTokenRequests[key] === request) {
            inFlightTokenRequests[key] = null;
        }
    });
    return request;
};

export const getAPI = async ({
    url,
    instance,
    expectedStatus,
}: Omit<FetchParams, "method" | "payload">): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });
        if (!token) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new AuthRequiredError(url);
        }

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        const headers = createHeaders(token);
        const options = {
            headers,
            method: "GET",
        };
        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

export const deleteAPI = async ({
    url,
    instance,
    expectedStatus,
}: Omit<FetchParams, "method" | "payload">): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });
        if (!token) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new AuthRequiredError(url);
        }

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        const headers = createHeaders(token);
        const options = {
            headers,
            method: "DELETE",
        };

        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

export const fetchWithAuth = async ({
    url,
    method,
    payload,
    instance,
    expectedStatus,
}: FetchParams): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });
        if (!token) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new AuthRequiredError(url);
        }

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        const headers = createHeaders(token);
        const options = {
            headers,
            method,
            body: payload ? JSON.stringify(payload) : undefined,
        };

        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

// Quick health check function
export const checkApiHealth = async (token?: string): Promise<boolean> => {
    // If we've detected a healthy state, trust it for the whole session
    if (healthCheckCache.isHealthy) {
        return true;
    }

    // For unhealthy state, check cache duration
    if (Date.now() - healthCheckCache.timestamp < CACHE_DURATION) {
        return false;
    }

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(BACKEND_URL, {
            method: "HEAD",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
        });

        clearTimeout(timeoutId);
        const isHealthy = response.ok;

        // Update cache
        healthCheckCache = {
            isHealthy,
            timestamp: Date.now(),
        };

        return isHealthy;
    } catch (error) {
        // Capture detailed error information for debugging
        Sentry.withScope((scope) => {
            scope.setTransactionName("checkApiHealth_internal");
            scope.setTag("error_type", "api_health_check_internal");
            scope.setExtra("backend_url", BACKEND_URL);
            scope.setExtra("error_name", error instanceof Error ? error.name : "Unknown");
            scope.setExtra("error_message", error instanceof Error ? error.message : String(error));
            scope.setExtra("has_token", !!token);
            scope.setExtra("cache_was_healthy", healthCheckCache.isHealthy);
            scope.setExtra("cache_age_ms", Date.now() - healthCheckCache.timestamp);

            // Don't capture AbortError (timeout) as critical - these are expected
            const level =
                error instanceof Error && error.name === "AbortError" ? "warning" : "error";

            Sentry.captureException(error, {
                level,
                tags: {
                    component: "checkApiHealth",
                    error_source:
                        error instanceof Error && error.name === "AbortError"
                            ? "timeout"
                            : "network_or_auth",
                },
            });
        });

        // Update cache with unhealthy state
        healthCheckCache = {
            isHealthy: false,
            timestamp: Date.now(),
        };
        return false;
    }
};
